import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { PrimaryButton, SecondaryButton, DangerButton } from '../../_core/_ui/buttons.component';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { IFormOption } from '../../_core/_ui/forms.component';
import { OtherContactsDisclosure } from '../../PlayerContact/other-contacts-disclosure.component';
import { PlayerContactFields } from '../../PlayerContact/player-contact-fields.component';
import { formatPhone } from '../../../lib/helpers/phone.helpers';
import { SPORT_INVOLVEMENT_OPTIONS } from '../../../lib/constants';
import {
  useParentApplyDefaultsToPlayerContact,
  useParentDeletePlayerContact,
  useParentUpdatePlayerContact,
} from '../../../lib/services/parent.service';
import {
  ContactRelationshipFormOptions,
  IParentPlayerContact,
  IParentSelf,
  Sport,
} from '../../../lib/types';
import {
  ContactRow,
  ContactSummary,
  ContactSummaryLabel,
  ContactPlayer,
  ContactCompany,
  ContactCredential,
  ContactEditPanel,
  ContactFields,
  ContactActions,
} from './parent-account.styles';
import { buildOptions } from './parent-player-contacts.utils';
import { IParentContactRowFields } from './parent-contact-row.types';
import { validationSchema } from './parent-contact-row.validation';

interface IEditorProps {
  parent: IParentSelf;
  contact: IParentPlayerContact;
  // Player-details path for this row's player, when the parent can reach one.
  playerDetailsPath?: string;
  // Co-contacts are resolved per athlete, so the card grants the disclosure to
  // one row per athlete rather than repeating it on every row it holds.
  showOtherContacts: boolean;
}

const findOption = (options: IFormOption[], value?: string | null) =>
  options.find((option) => option.value === (value || '')) || null;

// One PlayerContact row. Collapsed: single-line summary + edit button. Open: the
// shared contact field block, with the credentials constrained to the parent's
// verified lists — see "Parent verified-credentials list is the source of truth".
export const ParentPlayerContactEditor: React.FC<IEditorProps> = ({ parent, contact, playerDetailsPath, showOtherContacts }) => {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [updateContact] = useParentUpdatePlayerContact();
  const [applyDefaults, { loading: applyingDefaults }] = useParentApplyDefaultsToPlayerContact();
  const [deleteContact, { loading: deleting }] = useParentDeletePlayerContact();
  const { enqueueSnackbar } = useSnackbar();

  // Verified lists + a trailing "(none)" so the parent can unset this player's
  // contact field; an unverified current value is prepended as "(current)".
  const emailOptions = useMemo(
    () => buildOptions(parent.emails, contact.email, (v) => v, '(none) — don\'t email this player'),
    [parent.emails, contact.email],
  );
  const phoneOptions = useMemo(
    () => buildOptions(parent.phones, contact.phone, formatPhone, '(none) — don\'t text this player'),
    [parent.phones, contact.phone],
  );

  // Everyone else already on this athlete — the parent may point this row at a
  // credential a co-contact already carries, which the field block banners.
  const siblingContacts = useMemo(() => contact.otherContacts || [], [contact.otherContacts]);

  const toValues = useCallback((row: Partial<IParentPlayerContact>): IParentContactRowFields => ({
    relationship: findOption(ContactRelationshipFormOptions, row.relationship),
    sportInvolvement: findOption(SPORT_INVOLVEMENT_OPTIONS, row.sportInvolvement),
    email: findOption(emailOptions, row.email),
    phone: findOption(phoneOptions, row.phone),
  }), [emailOptions, phoneOptions]);

  const initialValues = useMemo(() => toValues(contact), [toValues, contact]);

  const handleSubmit = useCallback(async (
    values: IParentContactRowFields,
    { setSubmitting, resetForm }: FormikHelpers<IParentContactRowFields>,
  ) => {
    try {
      await updateContact({
        variables: {
          _id: contact._id,
          email: values.email?.value || '',
          phone: values.phone?.value || '',
          relationship: values.relationship?.value || null,
          // Hidden for non-default sports — omitted so it is left untouched
          // rather than cleared for an association that never collects it.
          sportInvolvement: contact.sport === Sport.default
            ? (values.sportInvolvement?.value || null)
            : undefined,
        },
      });
      enqueueSnackbar(`Updated contact for ${contact.playerName}.`, { variant: 'success' });
      resetForm({ values });
      setEditing(false);
    } catch (err) {
      console.error('parentUpdatePlayerContact failed:', err);
      enqueueSnackbar('Could not save changes. Please try again.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [updateContact, contact._id, contact.playerName, contact.sport, enqueueSnackbar]);

  const handleApplyDefaults = useCallback(async (form: FormikProps<IParentContactRowFields>) => {
    if (applyingDefaults) return;
    // The mutation overwrites the row from its response, so an unsaved
    // in-progress edit would be silently discarded — confirm first.
    if (form.dirty && !window.confirm(`Apply your default email and phone to ${contact.playerName}? Your unsaved changes for this player will be discarded.`)) {
      return;
    }
    try {
      const result = await applyDefaults({ variables: { _id: contact._id } });
      const next = result.data?.parentApplyDefaultsToPlayerContact;
      if (next) form.resetForm({ values: toValues(next) });
      enqueueSnackbar(`Applied your defaults to ${contact.playerName}.`, { variant: 'success' });
    } catch (err) {
      console.error('parentApplyDefaultsToPlayerContact failed:', err);
      enqueueSnackbar('Could not apply defaults. Please try again.', { variant: 'error' });
    }
  }, [applyingDefaults, applyDefaults, contact._id, contact.playerName, enqueueSnackbar, toValues]);

  const handleDelete = useCallback(async () => {
    if (deleting) return;
    try {
      await deleteContact({ variables: { _id: contact._id } });
      enqueueSnackbar(`Removed ${contact.playerName} from your account.`, { variant: 'success' });
      setConfirmingDelete(false);
      // The row evicts itself from cache via the mutation's `update` callback;
      // no need to close the editor here — the component unmounts.
    } catch (err) {
      console.error('parentDeletePlayerContact failed:', err);
      enqueueSnackbar('Could not remove that player. Please try again.', { variant: 'error' });
    }
  }, [deleting, deleteContact, contact._id, contact.playerName, enqueueSnackbar]);

  const canApplyDefault = Boolean(parent.emails[0] || parent.phones[0]);
  const formId = `parent-contact-row-${contact._id}`;

  return (
    <ContactRow>
      <ContactSummary>
        <ContactSummaryLabel>
          <ContactPlayer>{contact.playerName}</ContactPlayer>
          {/* The leading " — " is a ::before on ContactCompany so the mobile
              media query can drop it without conditional JSX. */}
          {contact.companyName && <ContactCompany>{contact.companyName}</ContactCompany>}
          {contact.email && <ContactCredential>{contact.email}</ContactCredential>}
          {contact.phone && <ContactCredential>{formatPhone(contact.phone)}</ContactCredential>}
        </ContactSummaryLabel>
        {playerDetailsPath && (
          <Tooltip title="Open player details">
            <IconButton
              size="small"
              component={Link}
              to={playerDetailsPath}
              aria-label={`Open player details for ${contact.playerName}`}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={editing ? 'Close editor' : 'Edit contact'}>
          <IconButton
            size="small"
            aria-label={editing ? `Stop editing ${contact.playerName}` : `Edit contact for ${contact.playerName}`}
            aria-expanded={editing}
            onClick={() => setEditing((open) => !open)}
          >
            {editing ? <ExpandLessIcon /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </ContactSummary>

      {showOtherContacts && <OtherContactsDisclosure id={contact.playerId} contacts={siblingContacts} />}

      <Collapse in={editing} timeout="auto" unmountOnExit>
        {/* Remounted on collapse (unmountOnExit), so closing the editor is what
            discards an unsaved edit — Cancel just closes it. */}
        <Formik<IParentContactRowFields>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(form) => (
            <Form id={formId}>
              <FocusFirstInvalidField formId={formId} />
              <ContactEditPanel>
                <ContactFields>
                  <PlayerContactFields
                    sport={contact.sport}
                    showName={false}
                    showHeight={false}
                    showSendToggles={false}
                    emailOptions={emailOptions}
                    phoneOptions={phoneOptions}
                    siblingContacts={siblingContacts}
                  />
                </ContactFields>
                <ContactActions>
                  <PrimaryButton
                    variant="contained"
                    type="submit"
                    disabled={form.isSubmitting}
                    startIcon={form.isSubmitting ? <CircularProgress size={16} /> : undefined}
                  >
                    Save
                  </PrimaryButton>
                  <SecondaryButton
                    variant="outlined"
                    onClick={() => handleApplyDefaults(form)}
                    disabled={!canApplyDefault || applyingDefaults}
                    startIcon={applyingDefaults ? <CircularProgress size={16} /> : undefined}
                  >
                    Apply default
                  </SecondaryButton>
                  <SecondaryButton variant="text" onClick={() => setEditing(false)} disabled={form.isSubmitting}>
                    Cancel
                  </SecondaryButton>
                  {/* Delete is the destructive action — pushed to the right via
                      marginLeft:auto so it sits visually apart from Save/Cancel. */}
                  <DangerButton
                    variant="outlined"
                    onClick={() => setConfirmingDelete(true)}
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : undefined}
                    style={{ marginLeft: 'auto' }}
                  >
                    Delete
                  </DangerButton>
                </ContactActions>
              </ContactEditPanel>
            </Form>
          )}
        </Formik>
      </Collapse>

      <Dialog
        open={confirmingDelete}
        onClose={() => !deleting && setConfirmingDelete(false)}
        aria-labelledby="confirm-delete-contact-title"
      >
        <DialogTitle id="confirm-delete-contact-title">Remove {contact.playerName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This unlinks {contact.playerName}
            {contact.companyName ? ` (${contact.companyName})` : ''} from your account. You won&apos;t
            see them on your dashboard or report cards until your association adds you back as a
            contact.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton variant="text" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
            Cancel
          </SecondaryButton>
          <DangerButton
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            Remove
          </DangerButton>
        </DialogActions>
      </Dialog>
    </ContactRow>
  );
};
